import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { BaseText, Button, Meta, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import MenuItem from 'components/common/MenuItem';
import Icon from 'components/icons/Icon';
import SkeletonLoaderRow, {
  SkeletonWrapper,
} from 'components/loading/SkeletonLoaderRow';
import { useAttachmentRow } from 'components/longTermParking/useAttachmentRow';
import Validity from 'components/permit/Validity';
import AccountContext from 'context/AccountContext';
import { isSameDay } from 'date-fns';
import { useAttachmentChanges } from 'hooks/useAttachmentChanges';
import usePmcPromotion from 'hooks/usePmcPromotion';
import usePollSalesOrderPaymentStatus from 'hooks/usePollSalesOrderPaymentStatus';
import { useSalesOrderAttachmentScreen } from 'hooks/useSalesOrderAttachmentScreen';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import useUpdateSalesOrder from 'hooks/useUpdateSalesOrder';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmCreateSalesOrderData } from 'states/common';
import { latestVehicleUsed } from 'states/persistInStorage';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePermitStore } from 'stores/permitStore';
import { useUiStore } from 'stores/uiStore';
import styled from 'styled-components/native';
import { CenterVerticalRow, StyledWrapper } from 'styles/ContainerStyles';
import {
  BillingFrequency,
  CountryCode,
  LicensePlate,
  LicensePlateInput,
  Maybe,
  ParkZone,
  SalesLocation,
  SalesOrderStatus,
  Vehicle,
  VehicleCountryCode,
} from 'types/generatedSchemaTypes';
import { LongTermStackParamList, RootNavProps } from 'types/navigation';
import {
  isRequiredSalesOrderAttachmentMissing,
  resolveAttachmentUi,
} from 'utils/attachmentUtils';
import {
  currencyFormatter,
  formatPriceForBillingFrequency,
  getCurrencyString,
  getMidnightUTCFromISOString,
  getParkingZoneName,
  getPriceForSalesOrder,
} from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import { updateStateAndStorage } from 'utils/MMKVStorageUtils';
import {
  determinePaymentType,
  getWalletCardMenuPresentation,
} from 'utils/paymentUtils';
import { getPromotionPricing, getPromotionText } from 'utils/promotionUtils';
import { testIdentifiers } from 'utils/testIdentifiers';

const RightAlignedText = styled(BaseText)`
  text-align: right;
`;

const LineThroughText = styled.Text`
  text-decoration-line: line-through;
  text-decoration-style: solid;
`;

const PriceColumn = styled.View`
  align-items: flex-end;
`;

const PermitPriceValue = ({
  originalPriceValue,
  discountedPriceValue,
  countryCode,
  billingFrequency,
  promotionText,
}: {
  originalPriceValue: number;
  discountedPriceValue: number;
  countryCode: CountryCode;
  billingFrequency: BillingFrequency;
  promotionText?: string;
}) => (
  <PriceColumn {...testIdentifiers('permit-info-price-amount')}>
    <RightAlignedText>
      <LineThroughText>
        <BaseText color={theme.colors.gray300}>
          {currencyFormatter(originalPriceValue, countryCode)}
        </BaseText>
      </LineThroughText>
      {' ' +
        formatPriceForBillingFrequency(
          discountedPriceValue,
          countryCode,
          billingFrequency
        )}
    </RightAlignedText>
    {promotionText && (
      <Meta
        color={theme.colors.gray300}
        {...testIdentifiers('permit-info-promotion-text')}>
        {promotionText}
      </Meta>
    )}
  </PriceColumn>
);

const PermitPriceAmount = ({
  promotionLoading,
  totalPrice,
  originalPriceValue,
  discountedPriceValue,
  countryCode,
  billingFrequency,
  promotionText,
}: {
  promotionLoading: boolean;
  totalPrice: string;
  originalPriceValue?: number;
  discountedPriceValue?: number;
  countryCode?: Maybe<CountryCode>;
  billingFrequency: BillingFrequency;
  promotionText?: string;
}) => {
  if (promotionLoading) {
    return (
      <SkeletonWrapper
        width={90}
        {...testIdentifiers('permit-info-price-loading')}>
        <SkeletonLoaderRow height={20} />
      </SkeletonWrapper>
    );
  }

  if (
    countryCode &&
    originalPriceValue !== undefined &&
    discountedPriceValue !== undefined
  ) {
    return (
      <PermitPriceValue
        originalPriceValue={originalPriceValue}
        discountedPriceValue={discountedPriceValue}
        countryCode={countryCode}
        billingFrequency={billingFrequency}
        promotionText={promotionText}
      />
    );
  }

  return (
    <RightAlignedText {...testIdentifiers('permit-info-price-amount')}>
      {totalPrice}
    </RightAlignedText>
  );
};

export function getPlaceName(
  parkZone: Maybe<ParkZone> | undefined,
  salesLocation: Maybe<SalesLocation> | undefined
): string {
  if (parkZone) {
    return getParkingZoneName(parkZone);
  }
  return salesLocation?.name || '';
}

export function isBuyButtonDisabled(
  currentOrderStatus: SalesOrderStatus | null | undefined
): boolean {
  return !currentOrderStatus || currentOrderStatus !== 'DRAFT';
}

export function getLicensePlateInputs(
  selectedLicensePlates: LicensePlate[],
  vehicle?: Vehicle | null
): LicensePlateInput[] {
  if (selectedLicensePlates.length > 0) {
    return selectedLicensePlates.map((plate) => ({
      plateText: plate.plateText ?? '',
      countryCode: (plate.countryCode as VehicleCountryCode) ?? 'FIN',
    }));
  } else if (vehicle?.licensePlate) {
    return [
      {
        plateText: vehicle.licensePlate.plateText ?? '',
        countryCode:
          (vehicle.licensePlate.countryCode as VehicleCountryCode) ?? 'FIN',
      },
    ];
  } else {
    return [];
  }
}

const ConfirmPurchasePermitModalContent: React.FC = () => {
  const { t } = useTranslation();
  const {
    refetchCustomer,
    refetchPermits,
    refetchCustomerSubscription,
    customer: { preferredLanguage },
  } = useContext(AccountContext);
  const { navigate, goBack, canGoBack } =
    useNavigation<StackNavigationProp<LongTermStackParamList>>();
  const { navigate: navigateRoot } = useNavigation<RootNavProps>();
  const {
    salesOrderId = '',
    orderLineId = '',
    version = 1,
    pmcId,
    quantity = 1,
    type,
    isFirstMonthFree,
    countryCode,
    prices,
    selectedPmc,
    meta: {
      parkZone = undefined,
      salesLocation = undefined,
      name = '',
      vehicle = undefined,
      paymentMethod = undefined,
      validity: { validFrom = new Date() } = {},
      parkingSpaceName = '',
      freeSalesOrder = false,
      billingFrequency = 'MONTHLY',
      automaticOnlineOrderActivation,
    } = {},
  } = confirmCreateSalesOrderData() || {};
  const { showOnSalesOrder, requireOnSalesOrder } = resolveAttachmentUi(
    selectedPmc?.attachmentPolicy
  );

  const selectedPaymentMethod = useSelectedPaymentMethod();
  const { cardId: savedCardId, lastNums: savedLastNums } =
    selectedPaymentMethod ?? {};
  const walletCard = getWalletCardMenuPresentation(
    savedCardId ? selectedPaymentMethod : paymentMethod,
    t
  );
  const { name: parkingSpaceSavedName, parkingSpaceUid } =
    useParkzoneStore((state) => state.selectedParkingSpace) ?? {};
  const savedStartDate =
    usePermitStore((state) => state.selectedPermitStartDate) ?? new Date();
  const { selectedBillingFrequency: savedBillingFrequency = 'MONTHLY' } =
    usePermitStore((state) => state.selectedPermitBillingInterval) ?? {};
  const selectedLicensePlates = usePermitStore(
    (state) => state.selectedLicensePlatesPermitRedeem
  );

  const { promotion, loading: promotionLoading } = usePmcPromotion(
    pmcId,
    determinePaymentType(selectedPaymentMethod ?? {})
  );

  const [updateType, setUpdateType] = useState<SalesOrderStatus | null>(null);
  const [initialOrderStatus, setInitialOrderStatus] =
    useState<SalesOrderStatus | null>(null);
  const [refetchCustomerLoading, setRefetchCustomerLoading] = useState(false);

  const { markChanged, attachmentUidsToSubmit } =
    useAttachmentChanges(salesOrderId);

  useSalesOrderAttachmentScreen({
    orderUid: salesOrderId,
    orderAttachments: undefined,
    enableSalesOrderAttachments: showOnSalesOrder,
    onAttachmentChanged: markChanged,
  });

  const {
    title: attachmentTitle,
    displayName: attachmentDisplayName,
    isInteractive: attachmentInteractive,
    handlePress: handleAttachmentPress,
    count: attachmentCount,
  } = useAttachmentRow();
  const requiredAttachmentMissing = isRequiredSalesOrderAttachmentMissing(
    requireOnSalesOrder,
    attachmentCount
  );

  const {
    currentPaymentStatus: currentPaymentState,
    currentOrderStatus,
    lastPollingCompleted,
  } = usePollSalesOrderPaymentStatus(
    salesOrderId,
    updateType,
    initialOrderStatus,
    false
  );

  const { updateSalesOrder } = useUpdateSalesOrder({
    customUpdate: undefined,
    customOnError: (error) => {
      showToast('error.permitCreationFailed', 'error');
      if (
        error.graphQLErrors.some(
          (e) => e.extensions?.code === 'SALES_ORDER_EXPIRED'
        )
      ) {
        confirmCreateSalesOrderData(undefined);
        useUiStore.getState().setSheetModal('');

        if (canGoBack()) {
          goBack();
        }
      }
    },
  });

  useEffect(() => {
    if (!lastPollingCompleted) {
      return;
    }

    if (
      currentPaymentState === 'SUCCESS_PAY' ||
      currentOrderStatus === 'WAITING_FOR_CSR_APPROVAL'
    ) {
      handlePermitPurchaseSuccess();
    } else if (currentPaymentState === 'FAILED_PAY') {
      handlePermitPurchaseFailed();
    }
  }, [currentPaymentState, lastPollingCompleted, currentOrderStatus]);

  const permitRefetch = () => {
    refetchPermits();
    useParkzoneStore.getState().setSelectedParkingSpace(undefined);
    usePermitStore.getState().setSelectedPermitStartDate(undefined);
    usePermitStore.getState().setSelectedPermitBillingInterval(undefined);
    usePermitStore.getState().setSelectedLicensePlatesPermitRedeem([]);
  };
  // Handle permit purchase success
  const handlePermitPurchaseSuccess = () => {
    if (type === 'LTP') {
      permitRefetch();
    }
    if (type === 'AIMO_PLUS') {
      refetchCustomerSubscription();
    }
    refetchCustomer();
    confirmCreateSalesOrderData(undefined);
    useUiStore.getState().setSheetModal('');

    showToast(
      type === 'LTP'
        ? t('success.permitPurchase')
        : t('success.subscriptionPurchase'),
      'success'
    );

    // Update latest vehicle used if single vehicle selected
    if (selectedLicensePlates.length === 1) {
      updateStateAndStorage(
        latestVehicleUsed,
        'latestVehicleUsed',
        selectedLicensePlates[0].plateText
      );
    }
    if (type === 'LTP') {
      navigate('completed', {
        orderStatus: currentOrderStatus ?? 'ACTIVATING',
      });
    }
  };

  // Handle permit purchase failed
  const handlePermitPurchaseFailed = () => {
    setRefetchCustomerLoading(true);
    refetchCustomer()
      ?.then(() => {
        useUiStore.getState().setSheetModal('');
      })
      .finally(() => {
        showToast('permit.paymentFailed', 'error');
        setRefetchCustomerLoading(false);
        if (type === 'LTP') {
          navigateRoot('mainTabs', {
            screen: 'servicesScreen',
            params: {
              screen: 'servicesMain',
            },
          });
        }
      });
  };

  const handleConfirmPurchase = () => {
    // Check for changes in permit details
    const checkChanges = {
      paymentMethod: savedCardId !== paymentMethod?.cardId,
      vehicles: selectedLicensePlates.length > 0,
      parkingSpace:
        Boolean(parkingSpaceName) && parkingSpaceSavedName !== parkingSpaceName,
      startDate: !isSameDay(validFrom, savedStartDate),
      billingFrequency: billingFrequency !== savedBillingFrequency,
    };

    const licensePlateInputs = getLicensePlateInputs(
      selectedLicensePlates,
      vehicle
    );

    const patchOrderStatus = automaticOnlineOrderActivation
      ? 'ACTIVATING'
      : 'WAITING_FOR_CSR_APPROVAL';

    updateSalesOrder({
      variables: {
        input: {
          updateType: 'PATCH',
          orderStatus: patchOrderStatus,
          salesOrderId,
          orderLineId,
          version,
          pmcId,
          quantity,
          creditCardId: savedCardId,
          registrationPlates: licensePlateInputs,
          parkingSpaceUid: parkingSpaceUid,
          hasChanges: Object.values(checkChanges).some(Boolean),
          validFrom: new Date(
            getMidnightUTCFromISOString(savedStartDate.toISOString())
          ),
          billingFrequency: savedBillingFrequency,
          ...(attachmentUidsToSubmit && {
            attachmentUids: attachmentUidsToSubmit,
          }),
        },
      },
    }).then(() => {
      setUpdateType('ACTIVATING');
      setInitialOrderStatus(currentOrderStatus ?? null);
    });
  };

  const placeName = getPlaceName(parkZone, salesLocation);

  const getVehiclesDisplay = () => {
    if (selectedLicensePlates.length > 0) {
      return selectedLicensePlates.length === 1
        ? selectedLicensePlates[0].plateText
        : t('permit.access.vehicles', {
            count: selectedLicensePlates.length,
          });
    }
    return vehicle?.name
      ? `${vehicle.name} (${vehicle?.licensePlate?.plateText})`
      : vehicle?.licensePlate?.plateText;
  };

  const isFreePrice =
    isFirstMonthFree && countryCode
      ? `0 ${getCurrencyString(countryCode)}`
      : null;

  const priceString =
    prices && countryCode
      ? getPriceForSalesOrder(
          prices,
          countryCode,
          savedStartDate ?? validFrom,
          billingFrequency ?? 'MONTHLY'
        )
      : null;

  const {
    promotionLike,
    hasActivePromotion,
    originalPriceValue,
    discountedPriceValue,
  } = getPromotionPricing(
    promotion,
    priceString?.totalPriceCents,
    priceString?.serviceFeeCents,
    savedStartDate ?? validFrom
  );
  const showDiscountedPrice =
    hasActivePromotion &&
    countryCode &&
    originalPriceValue !== undefined &&
    discountedPriceValue !== undefined;
  const promotionText = showDiscountedPrice
    ? getPromotionText(
        promotionLike,
        countryCode,
        preferredLanguage,
        'permit.promotion',
        t
      )
    : undefined;

  return (
    <>
      <StyledWrapper center mb={theme.normalising.heightPixel(30)}>
        <Meta
          center
          color={theme.colors.gray300}
          {...testIdentifiers('permit-info-meta')}>
          {t('services.confirmPermitDescription')}
        </Meta>
      </StyledWrapper>
      <StyledWrapper
        mb={theme.normalising.heightPixel(20)}
        center={isWeb}
        maxWidth={isWeb ? 600 : undefined}>
        {placeName && (
          <MenuItem
            color={theme.colors.gray300}
            testID="permit-info-location"
            menuLabel={t('services.location')}
            accessible={false}
            rightComponent={
              <RightAlignedText
                {...testIdentifiers('permit-info-location-text')}>
                {placeName}
              </RightAlignedText>
            }
            noBorder={false}
            key="permit-info-location"
          />
        )}
        <MenuItem
          color={theme.colors.gray300}
          testID="permit-info-type"
          accessible={false}
          menuLabel={
            type === 'AIMO_PLUS'
              ? t('account.aimoSubscription.subscriptionType')
              : t('services.permitType')
          }
          rightComponent={
            <RightAlignedText
              {...testIdentifiers('permit-info-type-text')}
              numberOfLines={2}>
              {name}
            </RightAlignedText>
          }
          noBorder={false}
          key="permit-info-type"
        />
        {getVehiclesDisplay() && type !== 'AIMO_PLUS' && (
          <MenuItem
            color={theme.colors.gray300}
            testID="permit-info-vehicle"
            accessible={false}
            menuLabel={t('services.permitVehicle')}
            rightComponent={
              <RightAlignedText
                {...testIdentifiers('permit-info-vehicle-text')}>
                {getVehiclesDisplay()}
              </RightAlignedText>
            }
            noBorder={false}
            key="permit-info-vehicle"
          />
        )}
        {parkingSpaceName && (
          <MenuItem
            color={theme.colors.gray300}
            testID="permit-info-space"
            accessible={false}
            menuLabel={t('longTermParking.parkingSpace')}
            rightComponent={
              <RightAlignedText {...testIdentifiers('permit-info-space-text')}>
                {parkingSpaceSavedName ?? parkingSpaceName}
              </RightAlignedText>
            }
            noBorder={false}
            key="permit-info-space"
          />
        )}
        {isFreePrice && (
          <MenuItem
            color={theme.colors.gray300}
            testID="permit-info-free-price"
            menuLabel={t('account.aimoSubscription.trialPeriod')}
            subtitle={t('account.aimoSubscription.thenPrice', {
              price: priceString?.totalPrice ?? '',
            })}
            accessible={false}
            rightComponent={
              <RightAlignedText
                {...testIdentifiers('permit-info-free-price-text')}>
                {isFreePrice}
              </RightAlignedText>
            }
            noBorder={false}
            key="permit-info-free-price"
          />
        )}
        {priceString && !isFreePrice && (
          <MenuItem
            color={theme.colors.gray300}
            testID="permit-info-price"
            menuLabel={t('price.priceTitle')}
            accessible={false}
            subtitle={priceString.serviceFee}
            rightComponent={
              <PermitPriceAmount
                promotionLoading={promotionLoading}
                totalPrice={priceString.totalPrice}
                originalPriceValue={originalPriceValue}
                discountedPriceValue={discountedPriceValue}
                countryCode={countryCode}
                billingFrequency={billingFrequency ?? 'MONTHLY'}
                promotionText={promotionText}
              />
            }
            noBorder={false}
            key="permit-info-price"
          />
        )}
        {paymentMethod && !freeSalesOrder && (
          <MenuItem
            color={theme.colors.gray300}
            testID="permit-info-payment"
            menuLabel={t('services.paymentMethod')}
            accessible={false}
            rightComponent={
              <CenterVerticalRow gap={5}>
                {walletCard && <Icon name={walletCard.iconName} size="m" />}
                <RightAlignedText
                  {...testIdentifiers('permit-info-payment-text')}>
                  {walletCard?.subtitle ??
                    `${t('payment.creditCardOnly')} **** ${savedLastNums ?? paymentMethod?.lastNums}`}
                </RightAlignedText>
              </CenterVerticalRow>
            }
            noBorder={false}
            key="permit-info-payment"
          />
        )}

        <MenuItem
          color={theme.colors.gray300}
          testID="permit-info-valid-from"
          menuLabel={t('longTermParking.valid')}
          accessible={false}
          rightComponent={
            savedStartDate || validFrom ? (
              <Validity
                testID="permit-info-valid-from-text"
                date={savedStartDate ?? validFrom}
              />
            ) : undefined
          }
          noBorder={true}
          key="permit-info-valid-from"
        />
      </StyledWrapper>
      {/* Web has no working attachment flow: no picker modal and no preview route. */}
      {showOnSalesOrder && salesOrderId && !isWeb ? (
        <StyledWrapper mb={theme.normalising.heightPixel(20)}>
          <MenuItem
            color={theme.colors.gray300}
            testID="permit-info-attachment"
            menuLabel={attachmentTitle}
            accessible={false}
            rightComponent={
              <RightAlignedText
                {...testIdentifiers('permit-info-attachment-text')}>
                {attachmentDisplayName}
              </RightAlignedText>
            }
            onPress={attachmentInteractive ? handleAttachmentPress : undefined}
            noBorder={true}
            key="permit-info-attachment"
          />
        </StyledWrapper>
      ) : null}
      <StyledWrapper>
        <Button
          loading={!lastPollingCompleted || refetchCustomerLoading}
          {...testIdentifiers('permit-confirm-buy-button')}
          text={freeSalesOrder ? t('button.redeem') : t('button.buy')}
          size="l"
          buttonType="secondary"
          onPress={handleConfirmPurchase}
          disabled={
            isBuyButtonDisabled(currentOrderStatus) || requiredAttachmentMissing
          }
        />
      </StyledWrapper>
    </>
  );
};
export default ConfirmPurchasePermitModalContent;
